import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Avatar,
  Badge,
  Divider,
} from '@mui/material';
import {
  IconLayoutDashboard,
  IconMessage2,
  IconInbox,
  IconAddressBook,
  IconSparkles,
  IconDatabase,
  IconBrandGoogle,
  IconAffiliate,
  IconBrandWhatsapp,
  IconMail,
} from '@tabler/icons-react';

import { green, ink, radii } from '../../theme/abisinyaTheme';
import { APP_FONT_FAMILY } from '../../theme/appTypography';

const DRAWER_WIDTH = 240;

// ── Nav structure ─────────────────────────────────────────────────────────────
// `key` must match the selectedMenu strings used in renderContent() switch.
// `displayLabel` is shown in the UI.
// `icon` is a render function (size) => ReactNode.
// `colored` = true means the icon has its own colours and needs the invert filter when active.

const NAV_GROUPS = [
  {
    label: 'Workspace',
    items: [
      {
        key: 'Dashboard',
        displayLabel: 'Dashboard',
        icon: (sz) => <IconLayoutDashboard size={sz} stroke={1.6} />,
      },
      {
        key: 'Inquiry Chats',
        displayLabel: 'Inquiry chats',
        icon: (sz) => <IconMessage2 size={sz} stroke={1.6} />,
      },
      {
        key: 'Unibox',
        displayLabel: 'Unibox',
        icon: (sz) => <IconInbox size={sz} stroke={1.6} />,
      },
    ],
  },
  {
    label: 'Contacts',
    items: [
      {
        key: 'My Contacts',
        displayLabel: 'My contacts',
        icon: (sz) => <IconAddressBook size={sz} stroke={1.6} />,
      },
      {
        key: 'Inquiries Contacts',
        displayLabel: 'Inquiry contacts',
        icon: (sz) => <IconAddressBook size={sz} stroke={1.6} />,
      },
      {
        key: 'Google Contacts',
        displayLabel: 'Google contacts',
        icon: (sz) => <IconBrandGoogle size={sz} stroke={1.6} />,
      },
      {
        key: 'HubSpot Contacts',
        displayLabel: 'HubSpot contacts',
        icon: (sz) => <IconAffiliate size={sz} stroke={1.6} />,
      },
    ],
  },
  {
    label: 'Broadcast',
    items: [
      {
        key: 'WhatsApp Broadcasts',
        displayLabel: 'WhatsApp',
        icon: (sz) => <IconBrandWhatsapp size={sz} stroke={1.6} />,
      },
      {
        key: 'Mass Emailing',
        displayLabel: 'Mass email',
        icon: (sz) => <IconMail size={sz} stroke={1.6} />,
      },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      {
        key: 'Intelligence Hub',
        displayLabel: 'Intelligence hub',
        icon: (sz) => <IconSparkles size={sz} stroke={1.6} />,
      },
      {
        key: 'Customer Database',
        displayLabel: 'Customer database',
        icon: (sz) => <IconDatabase size={sz} stroke={1.6} />,
      },
    ],
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function BrandMark() {
  return (
    <Box sx={{ px: 2, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {/* Green square with upright bold "A" — matches the consultant dashboard brand */}
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: '12px',
          bgcolor: '#2db838',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontFamily: APP_FONT_FAMILY,
            fontWeight: 700,
            fontSize: 24,
            lineHeight: 1,
            color: '#fff',
          }}
        >
          A
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: APP_FONT_FAMILY,
            fontWeight: 800,
            fontSize: '1.18rem',
            color: '#163126',
            lineHeight: 1.1,
            letterSpacing: '-0.04em',
          }}
        >
          Abisinya
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: 11,
            color: ink[500],
            lineHeight: 1,
            mt: '2px',
          }}
        >
          Marketing
        </Typography>
      </Box>
    </Box>
  );
}

function UserCard({ user, displayName }) {
  const initial = displayName?.charAt(0)?.toUpperCase() || '?';
  return (
    <Box sx={{ px: 1.5, pb: 1.5 }}>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          borderRadius: `${radii.md}px`,
          border: `1px solid ${ink[200]}`,
          bgcolor: '#fff',
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          variant="dot"
          color="success"
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: green[50],
              color: green[700],
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: 14,
            }}
          >
            {initial}
          </Avatar>
        </Badge>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: 13,
              color: ink[900],
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 11,
              color: ink[500],
              lineHeight: 1,
            }}
          >
            Marketing team
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function NavItem({ item, isActive, badgeCount, onSelect }) {
  const ICON_SIZE = 24;
  return (
    <ListItemButton
      selected={isActive}
      onClick={() => onSelect(item.key)}
      sx={{
        mx: 1.25,
        mb: 0.5,
        borderRadius: '14px',
        py: '9px',
        px: '14px',
        minHeight: 44,
        gap: 1.35,
        color: isActive ? '#fff' : '#163126',
        boxShadow: isActive ? '0 16px 28px -20px rgba(45, 184, 56, 0.95)' : 'none',
        '&.Mui-selected': {
          bgcolor: '#2db838',
          color: '#fff',
          '&:hover': { bgcolor: '#24952e' },
          '& svg': { color: '#fff' },
        },
        '&:hover:not(.Mui-selected)': { bgcolor: 'rgba(45, 184, 56, 0.08)' },
        transition: 'background-color 0.15s, box-shadow 0.15s',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          lineHeight: 0,
        }}
      >
        {item.icon(ICON_SIZE)}
      </Box>

      <ListItemText
        primary={item.displayLabel}
        sx={{
          m: 0,
          // Use sx (NOT primaryTypographyProps) so this beats the abisinya theme's
          // MuiListItemText.primary styleOverride (0.8125rem), which otherwise forces
          // every nav label to 13px and ignored the size set via primaryTypographyProps.
          '& .MuiListItemText-primary': {
            fontFamily: APP_FONT_FAMILY,
            fontSize: 15,
            fontWeight: isActive ? 600 : 500,
            lineHeight: 1.3,
            color: 'inherit',
          },
        }}
      />

      {badgeCount > 0 && (
        <Box
          component="span"
          sx={{
            minWidth: 20,
            height: 20,
            px: 0.75,
            borderRadius: '10px',
            bgcolor: isActive ? 'rgba(255,255,255,0.22)' : green[100],
            color: isActive ? '#fff' : green[700],
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: '"Inter", sans-serif',
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </Box>
      )}
    </ListItemButton>
  );
}

// ── Drawer contents ───────────────────────────────────────────────────────────

function SidebarContent({ selectedMenu, onMenuSelect, user, displayName, badgeCounts }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <BrandMark />
      <Divider sx={{ borderColor: ink[200], mx: 1.5, mb: 1 }} />
      <UserCard user={user} displayName={displayName} />

      <List sx={{ flexGrow: 1, overflowY: 'auto', py: 0 }}>
        {NAV_GROUPS.map((group, gi) => (
          <React.Fragment key={group.label}>
            {gi > 0 && (
              <Divider sx={{ borderColor: ink[200], mx: 2, my: '6px' }} />
            )}
            <Typography
              sx={{
                px: '20px',
                pt: gi === 0 ? '4px' : '2px',
                pb: '4px',
                fontFamily: '"Inter", sans-serif',
                fontSize: 11,
                fontWeight: 700,
                color: ink[400],
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              {group.label}
            </Typography>
            {group.items.map((item) => (
              <NavItem
                key={item.key}
                item={item}
                isActive={selectedMenu === item.key}
                badgeCount={badgeCounts?.[item.key] || 0}
                onSelect={onMenuSelect}
              />
            ))}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
}

// ── Public component ──────────────────────────────────────────────────────────

export default function AppSidebar({
  selectedMenu,
  onMenuSelect,
  user,
  displayName,
  badgeCounts,
  open,
  onClose,
  isDesktop,
}) {
  const drawerSx = {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: DRAWER_WIDTH,
      boxSizing: 'border-box',
      bgcolor: '#ffffff',
      border: 'none',
      borderRight: `1px solid ${ink[200]}`,
      overflowX: 'hidden',
    },
  };

  if (isDesktop) {
    return (
      <Drawer variant="permanent" open sx={drawerSx}>
        <SidebarContent
          selectedMenu={selectedMenu}
          onMenuSelect={onMenuSelect}
          user={user}
          displayName={displayName}
          badgeCounts={badgeCounts}
        />
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={drawerSx}
    >
      <SidebarContent
        selectedMenu={selectedMenu}
        onMenuSelect={onMenuSelect}
        user={user}
        displayName={displayName}
        badgeCounts={badgeCounts}
      />
    </Drawer>
  );
}

export { DRAWER_WIDTH };
